import React, { useRef, useEffect } from 'react';

const AudioVisualizer = ({ stream, isRecording, isRecordingActive }) => {
  const canvasRef = useRef(null);
  // Use refs to hold animation state and audio nodes to prevent re-initialization
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const animationFrameIdRef = useRef(null);

  // This effect handles the drawing loop (start, pause, resume)
  useEffect(() => {
    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');

    const draw = () => {
      if (!analyserRef.current) return;

      const analyser = analyserRef.current;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      const barWidth = 3;
      const gap = 1;
      const step = barWidth + gap;

      // --- Drawing logic (same as before) ---
      const imageData = canvasCtx.getImageData(step, 0, canvas.width - step, canvas.height);
      canvasCtx.putImageData(imageData, 0, 0);
      canvasCtx.clearRect(canvas.width - step, 0, step, canvas.height);
      
      analyser.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (const amplitude of dataArray) {
        sum += Math.abs(amplitude - 128);
      }
      const average = sum / bufferLength;
      
      const barHeight = average * 2.0; // Slightly adjusted multiplier
      const y = canvas.height / 2 - barHeight / 2;
      
      canvasCtx.fillStyle = '#374151'; // Dark Slate Gray
      canvasCtx.fillRect(canvas.width - barWidth, y, barWidth, Math.max(1, barHeight)); // Ensure min height of 1
      
      // Request the next frame
      animationFrameIdRef.current = requestAnimationFrame(draw);
    };

    // --- Control the animation based on isRecordingActive ---
    if (isRecordingActive) {
      // Start drawing if it's not already running
      if (animationFrameIdRef.current === null) {
        animationFrameIdRef.current = requestAnimationFrame(draw);
      }
    } else {
      // Stop drawing if it is running
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    }

    // Cleanup on component unmount
    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, [isRecordingActive]); // This effect ONLY responds to the active state

  // This effect handles setting up and tearing down the audio context
  useEffect(() => {
    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');
    
    const setupAudio = () => {
      if (stream && !audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.fftSize = 256;
      }
    };
    
    const clearCanvas = () => {
        canvasCtx.fillStyle = '#F3F4F6'; // Background color
        canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
    };
    
    // Size the canvas correctly
    const container = canvas.parentElement;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    clearCanvas();

    if (isRecording) {
      setupAudio();
    } else {
      // When not recording at all, clear the canvas
      clearCanvas();
    }
    
    // Cleanup audio context on unmount or when stream is removed
    return () => {
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, [stream, isRecording]); // This effect responds to the session starting/stopping

  return <canvas ref={canvasRef} />;
};

export default AudioVisualizer;